import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

// 5-day idle policy: a user remains logged in indefinitely as long as they use
// the site at least once every 5 days. After 5 days of inactivity their token
// is wiped on next page load.
const IDLE_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;
const LAST_ACTIVE_KEY = "zumroo_last_active";
const TOKEN_KEY = "zumroo_user_token";
const USER_KEY = "zumroo_user";

function readStoredToken() {
  try {
    const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || "0", 10);
    if (last && Date.now() - last > IDLE_WINDOW_MS) {
      // Idle for >5 days — purge stored credentials
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(LAST_ACTIVE_KEY);
      return null;
    }
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function bumpActivity() {
  try { localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now())); } catch { /* storage quota / safari private */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      // Trigger the idle check before reading user — keeps both in sync
      readStoredToken();
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => readStoredToken());
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState("/listings");
  const [pendingState, setPendingState] = useState(null);

  const isAuthed = !!token;

  const requireAuth = useCallback(
    ({ redirectTo = "/listings", state = null } = {}) => {
      if (token) {
        bumpActivity();
        return true;
      }
      setPendingRedirect(redirectTo);
      setPendingState(state);
      setModalOpen(true);
      return false;
    },
    [token]
  );

  const openLogin = useCallback((opts = {}) => requireAuth(opts), [requireAuth]);
  const closeLogin = useCallback(() => setModalOpen(false), []);

  const completeLogin = useCallback((nextToken, nextUser) => {
    if (nextToken) {
      localStorage.setItem(TOKEN_KEY, nextToken);
      setToken(nextToken);
    }
    if (nextUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      setUser(nextUser);
    }
    bumpActivity();
    setModalOpen(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const sync = () => {
      const raw = readStoredToken();
      if (raw !== token) {
        setToken(raw || null);
        if (!raw) setUser(null);
      }
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [token]);

  // Heartbeat: bump last_active on every navigation, click and visibility change.
  // Cheap throttle — write at most once every 30s.
  useEffect(() => {
    if (!token) return;
    let last = 0;
    const heartbeat = () => {
      const now = Date.now();
      if (now - last > 30000) {
        last = now;
        bumpActivity();
      }
    };
    // Initial bump on mount (page load while authed)
    bumpActivity();
    window.addEventListener("click", heartbeat, { passive: true });
    window.addEventListener("keydown", heartbeat, { passive: true });
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      window.removeEventListener("click", heartbeat);
      window.removeEventListener("keydown", heartbeat);
      document.removeEventListener("visibilitychange", heartbeat);
    };
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthed,
        modalOpen,
        pendingRedirect,
        pendingState,
        requireAuth,
        openLogin,
        closeLogin,
        completeLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Helper hook to navigate after login modal closes successfully
export function useAuthNavigate() {
  const { pendingRedirect, pendingState } = useAuth();
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(pendingRedirect || "/listings", { state: pendingState || undefined });
  }, [navigate, pendingRedirect, pendingState]);
}
